import { DateTime } from 'luxon';
import { PayCycleEstimate } from '@thera-hr/api-sdk/api/resources/api/resources/thera/resources/employment';
import { TeamAndDetails } from '@thera-hr/api-sdk/api/resources/api/resources/thera/resources/company';

import { ICompany, IContract, IInvoice, IInvoiceInfo } from 'types';
import type { IEmployeeContract } from 'hooks/useEmployeeContractSWR';
import { dateStringToSpecifiedDateFormat, dateStringToinvoiceDateFormat } from './dateTime';
import {
  DueStatus,
  InvoiceStatus,
  ItemTypeInvoices,
  contractTypeLabel,
  contractTypeMap,
  invoiceStatusTextStyle,
} from 'lib/constants';
import { IInvoiceWithContractAndCompany } from 'components/app-components/invoice/ContractorInvoiceHistoryTable';
import { IMilestone } from 'components/app-components/add-contractor/types';
import { captureErrorInDatadog } from './error';
import { getTeamByContractId } from './rolesPermissions';
import { startCase } from 'lodash';
import { dineroCurrencyMapping } from 'lib/dinero-mapping';

export type GroupByField = 'contractor' | 'currency' | 'contractType' | 'payFrequency' | 'payCycleDate';
export type GroupByFieldV2 = 'CONTRACTOR' | 'CONTRACT';

export interface IInvoiceStats {
  userID?: string;
  firstName?: string;
  lastName?: string;
  totalAmount: number;
  contractID: string;
  approvedCount: number;
  overdueCount: number;
  processingCount: number;
  pendingReviewCount: number;
  paidCount: number;
  voidOrFailedCount: number;
  contractType?: string;
  invoiceCycleStartDate: string;
  invoiceCycleEndDate: string;
  invoiceCycleDueDate: string;
  currency: string;
  /**
   * If there are multiple currencies in the invoices, this will contain the breakdown.
   * If only one currency, this will be a single entry matching totalAmount/currency.
   */
  currencyAmounts: Array<{ currency: string; amount: number }>;
  groupType: 'PAYCYCLE' | 'ITEMORONEOFF' | 'MILESTONE';
  allPaid: boolean;
  status: 'PAID' | 'OVERDUE' | 'DUE' | 'PROCESSING' | 'VOID';
  paymentTimestamp: number;
  type: IInvoice['type'] | 'One-Off Payment';
  updatedAt: number;
}

export function filterInvoices(invoices: IInvoiceInfo[], searchTerm: string) {
  if (!searchTerm) return invoices;
  // ensure the search term is lower case
  const lowerCaseSearchTerm = searchTerm?.toLowerCase();

  // filter the invoices
  return invoices.filter((invoice) => {
    const recipientUser = invoice.recipientUser;

    // if there is no recipient user, filter out this invoice
    if (!recipientUser) return false;

    // check if the search term is in any of the fields (firstName, lastName, email)
    return (
      recipientUser.firstName.toLowerCase().includes(lowerCaseSearchTerm) ||
      recipientUser.lastName.toLowerCase().includes(lowerCaseSearchTerm) ||
      recipientUser.email.toLowerCase().includes(lowerCaseSearchTerm)
    );
  });
}

// not used, see below
export function groupInvoices_(invoiceInfos: IInvoiceInfo[], groupBy: GroupByField): Record<string, IInvoiceInfo[]> {
  return invoiceInfos.reduce((grouped: Record<string, IInvoiceInfo[]>, invoiceInfo: IInvoiceInfo) => {
    let groupByKey: string | undefined;

    if (groupBy === 'contractor') {
      groupByKey = invoiceInfo.recipientUser?.userID;
    } else if (groupBy === 'currency') {
      groupByKey = invoiceInfo.invoice?.currency;
    } else if (groupBy === 'contractType') {
      groupByKey = invoiceInfo.contract?.contractType;
    } else if (groupBy === 'payFrequency') {
      groupByKey = invoiceInfo.contract?.wageType;
    }

    if (!invoiceInfo.invoice || !groupByKey) return grouped;

    return {
      ...grouped,
      [groupByKey]: grouped[groupByKey] ? [...grouped[groupByKey], invoiceInfo] : [invoiceInfo],
    };
  }, {});
}

// same as above
export function groupInvoices(
  invoiceInfos: IInvoiceInfo[],
  groupBy: GroupByField,
  groupSameTypeTogether?: boolean,
): Record<string, Record<string, IInvoiceInfo[]>> {
  if (!invoiceInfos?.length) return;

  const groupedByKey: Record<string, IInvoiceInfo[]> = {};
  const groupedByInvoiceType: Record<string, Record<string, IInvoiceInfo[]>> = {};

  // group invoices by key
  for (const invoiceInfo of invoiceInfos) {
    let groupByKey: string | undefined;

    if (groupBy === 'contractor') {
      groupByKey = invoiceInfo.recipientUser?.userID;
    } else if (groupBy === 'currency') {
      groupByKey = invoiceInfo.invoice?.currency;
    } else if (groupBy === 'contractType') {
      groupByKey = invoiceInfo.contract?.contractType;
    } else if (groupBy === 'payFrequency') {
      groupByKey = invoiceInfo.contract?.wageType;
    }

    if (!invoiceInfo.invoice || !groupByKey) continue;

    if (!groupedByKey[groupByKey]) {
      groupedByKey[groupByKey] = [];
    }

    groupedByKey[groupByKey].push(invoiceInfo);
  }

  // all invoices of the type MILESTONE will be grouped together, same with ONE-OFF and ITEM TYPE [if they don't have a pay-cycle]
  if (groupSameTypeTogether) {
    // group the grouped (by key) invoices by invoice type
    Object.entries(groupedByKey).forEach(([key, invoices]) => {
      let groupByKey: string | undefined;
      const _groupedInvoices: Record<string, IInvoiceInfo[]> = {};

      for (const invoiceInfo of invoices) {
        if (invoiceInfo.invoice?.startDate) {
          // Invoices can be a regular invoice and item type but not one-off
          groupByKey = `${invoiceInfo.recipientUser?.userID}_PAYCYCLE_${invoiceInfo.invoice?.startDate}_${invoiceInfo.invoice?.endDate}`;
        } else if (isMilestoneInvoice(invoiceInfo)) {
          // Checks if the contractor is Milestone type and invoice is Milestone type
          // Invoices can be a regular invoice but not one-off or item type
          groupByKey = `MILESTONE`;
        } else if (isItemTypeInvoice(invoiceInfo)) {
          groupByKey = `ITEMTYPE`;
        } else {
          groupByKey = `ONEOFF`;
        }

        if (!invoiceInfo.invoice || !groupByKey) continue;
        if (!_groupedInvoices[groupByKey]) {
          _groupedInvoices[groupByKey] = [];
        }
        _groupedInvoices[groupByKey].push(invoiceInfo);
      }
      groupedByInvoiceType[key] = _groupedInvoices;
    });
  } else {
    // group the grouped (by key) invoices by invoice type
    Object.entries(groupedByKey).forEach(([key, invoices]) => {
      let groupByKey: string | undefined;
      const _groupedInvoices: Record<string, IInvoiceInfo[]> = {};

      for (const invoiceInfo of invoices) {
        if (invoiceInfo.invoice?.startDate) {
          // Invoices can be a regular invoice and item type but not one-off
          groupByKey = `${invoiceInfo.recipientUser?.userID}_PAYCYCLE_${invoiceInfo.invoice?.startDate}_${invoiceInfo.invoice?.endDate}`;
        } else if (isMilestoneInvoice(invoiceInfo)) {
          // Checks if the contractor is Milestone type and invoice is Milestone type
          // Invoices can be a regular invoice but not one-off or item type
          groupByKey = `MILESTONE_${invoiceInfo.invoice?.displayID}`;
        } else {
          groupByKey = `ITEMORONEOFF_${invoiceInfo.invoice?.displayID}`;
        }

        if (!invoiceInfo.invoice || !groupByKey) continue;
        if (!_groupedInvoices[groupByKey]) {
          _groupedInvoices[groupByKey] = [];
        }
        _groupedInvoices[groupByKey].push(invoiceInfo);
      }
      groupedByInvoiceType[key] = _groupedInvoices;
    });
  }
  return groupedByInvoiceType;
}

export function groupInvoicesByCompanyName(
  invoices: IInvoiceWithContractAndCompany[],
): Record<string, Record<string, IInvoiceWithContractAndCompany[]>> {
  const groupedByKey: Record<string, IInvoiceWithContractAndCompany[]> = {};
  const groupedByInvoiceType: Record<string, Record<string, IInvoiceWithContractAndCompany[]>> = {};

  // group invoices by key
  for (const invoice of invoices) {
    const groupByKey = invoice?.company?.companyName;
    if (!invoice || !groupByKey) continue;
    if (!groupedByKey[groupByKey]) {
      groupedByKey[groupByKey] = [];
    }
    groupedByKey[groupByKey].push(invoice);
  }

  // group the grouped (by key) invoices by invoice type
  Object.entries(groupedByKey).forEach(([key, invoicesWithContractAndCompany]) => {
    let groupByKey: string | undefined;
    const _groupedInvoices: Record<string, IInvoiceWithContractAndCompany[]> = {};

    for (const invoiceWithContractAndCompany of invoicesWithContractAndCompany) {
      if (invoiceWithContractAndCompany?.invoice.startDate) {
        // Invoices can be a regular invoice and item type but not one-off
        groupByKey = `${invoiceWithContractAndCompany.company.companyName}_PAYCYCLE_${invoiceWithContractAndCompany?.invoice.startDate}_${invoiceWithContractAndCompany?.invoice.endDate}`;
      } else if (invoiceWithContractAndCompany.invoice.type === 'MILESTONE') {
        // Checks if the contractor is Milestone type and invoice is Milestone type
        // Invoices can be a regular milestone invoice but not one-off or item type
        groupByKey = `MILESTONE_${invoiceWithContractAndCompany?.invoice.displayID}`;
      } else {
        groupByKey = `ITEMORONEOFF_${invoiceWithContractAndCompany?.invoice.displayID}`;
      }

      if (!invoiceWithContractAndCompany || !groupByKey) continue;
      if (!_groupedInvoices[groupByKey]) {
        _groupedInvoices[groupByKey] = [];
      }
      _groupedInvoices[groupByKey].push(invoiceWithContractAndCompany);
    }
    groupedByInvoiceType[key] = _groupedInvoices;
  });
  return groupedByInvoiceType;
}

export const checkIfInvoiceOverdue = (paymentDueDate: string): boolean => {
  if (!paymentDueDate) return false;
  const dueDate = DateTime.fromISO(paymentDueDate, { zone: 'utc' }).toISODate();
  const now = DateTime.utc().toISODate();
  return now > dueDate;
};

export const checkIfInvoiceDue = (paymentDueDate: string): boolean => {
  if (!paymentDueDate) return false;
  const dueDate = DateTime.fromISO(paymentDueDate, { zone: 'utc' }).toISODate();
  const now = DateTime.utc().toISODate();
  return now === dueDate;
};

/**
 * Returns integer number of days until the due date in UTC, comparing *dates* (not times).
 *
 * Examples:
 * - due today => 0
 * - due tomorrow => 1
 * - overdue (yesterday) => -1
 *
 * Note: Using date-only comparisons avoids fractional day differences that can cause
 * inconsistent UI counts across pages.
 */
export const getInvoiceDaysUntilDueDateUtc = (paymentDueDate?: string): number | null => {
  if (!paymentDueDate) return null;
  const dueDate = DateTime.fromISO(paymentDueDate, { zone: 'utc' }).startOf('day');
  const now = DateTime.utc().startOf('day');
  return dueDate.diff(now, 'days').days;
};

/**
 * Canonical "due" logic used across Contractors / Payroll / Review / Pay pages.
 *
 * "Due" means the invoice is actionable in the main list:
 * - no due date (always due)
 * - due today / overdue
 * - within the configured allowed days before due date (inclusive)
 *
 * If `paymentAllowedDaysBeforeDueDate` is not configured, future-due invoices are NOT due.
 */
export const isInvoiceDueOrWithinAllowedWindow = (
  paymentDueDate: string | undefined,
  paymentAllowedDaysBeforeDueDate: number | null | undefined,
): boolean => {
  // If no due date, allow the invoice.
  if (!paymentDueDate) return true;

  const daysUntilDue = getInvoiceDaysUntilDueDateUtc(paymentDueDate);
  if (daysUntilDue === null) return true;

  // If invoice is past due (negative days) or due today, always allow it.
  if (daysUntilDue <= 0) return true;

  // If the setting isn't configured, future-due invoices should not be treated as due.
  if (paymentAllowedDaysBeforeDueDate === undefined || paymentAllowedDaysBeforeDueDate === null) return false;

  // Allow invoice if within the allowed days before due date (inclusive).
  return paymentAllowedDaysBeforeDueDate >= daysUntilDue;
};

/**
 * Calculates the display status for contractor invoice views.
 * For PROCESSING invoices, returns OVERDUE, DUE, or OPEN based on the due date.
 * Admin users see the original status without transformation.
 *
 * @param status - The current invoice status
 * @param paymentDueDate - The payment due date (ISO format string)
 * @param isAdminPoV - Optional flag indicating if this is an admin view (defaults to false)
 * @returns The display status string
 */
export const getInvoiceDisplayStatusForContractors = (
  status: string,
  paymentDueDate: string,
  isAdminPoV: boolean = false,
): string => {
  // Admin users see the original status
  if (isAdminPoV) return status;

  // For contractors, transform PROCESSING status based on due date
  if (status === 'PROCESSING') {
    if (checkIfInvoiceOverdue(paymentDueDate)) {
      return 'OVERDUE';
    } else if (checkIfInvoiceDue(paymentDueDate)) {
      return 'DUE';
    } else {
      return 'OPEN';
    }
  }

  return status;
};

export interface IContractorInvoiceModalContext {
  companyName: string;
  companyID: string;
  milestone: IMilestone;
  contract: IContract;
}

/**
 * Resolves what InvoiceSideModalContractor needs for one invoice out of the worker's contract list.
 * The modal takes a narrowed IContract instead of the full IEmployment, so the pick happens here rather
 * than at every call site.
 */
export const getContractorInvoiceModalContext = (
  invoice: IInvoice,
  employeeContracts: IEmployeeContract[],
): IContractorInvoiceModalContext => {
  const employeeContract = employeeContracts?.find((item) => item?.contract?.id === invoice?.contractID);

  return {
    companyName: employeeContract?.company?.companyName,
    companyID: employeeContract?.company?.companyID,
    milestone: employeeContract?.contract?.milestoneDetails?.milestones?.[invoice?.milestoneID],
    contract: {
      contractType: employeeContract?.contract?.contractType,
      wageType: employeeContract?.contract?.wageType,
    },
  };
};

export const invoiceDateRangeReadableDate = (invoice: IInvoice) => {
  if (!invoice?.startDate || !invoice?.endDate) return '';
  return `${dateStringToinvoiceDateFormat(invoice.startDate, 'd LLL')} - ${dateStringToinvoiceDateFormat(
    invoice.endDate,
    'd LLL',
  )}`;
};

export const getContractorName = (contractorID: string, invoiceInfo: IInvoiceInfo) => {
  const user = invoiceInfo?.recipientUser;
  return user?.firstName + ' ' + user?.lastName;
};

export const getGroupByItemTitle = (groupBy: GroupByField, key: string, invoiceInfo: IInvoiceInfo) => {
  if (groupBy === 'contractor') return getContractorName(key, invoiceInfo);
  if (groupBy === 'contractType') return contractTypeLabel[key];
  if (groupBy === 'payCycleDate') {
    const invoiceType = key.split('_')[1];
    if (invoiceType === 'ITEMORONEOFF') return 'One-Off Payment';
    return `${dateStringToSpecifiedDateFormat(key.split('_')[2], 'LLL d')} - ${dateStringToSpecifiedDateFormat(
      key.split('_')[3],
      'LLL d',
    )}`;
  }
  return key;
};

export const getInvoiceContractType = (invoiceArg: IInvoiceInfo | IInvoiceWithContractAndCompany) =>
  invoiceArg?.invoice?.contractType || invoiceArg?.contract?.contractType;

export const getInvoiceWageType = (invoiceArg: IInvoiceInfo | IInvoiceWithContractAndCompany) =>
  invoiceArg?.invoice?.wageType || invoiceArg?.contract?.wageType;

export const isFlexibleRateWorkInvoice = (invoiceArg: IInvoiceInfo | IInvoiceWithContractAndCompany) => {
  return (
    invoiceArg?.invoice?.startDate &&
    invoiceArg?.invoice?.type === 'INVOICE' &&
    getInvoiceContractType(invoiceArg) === 'FLEXIBLE_RATE'
  );
};

export const isTimeTrackingWorkInvoice = (invoiceArg: IInvoiceInfo | IInvoiceWithContractAndCompany) => {
  return (
    invoiceArg?.invoice?.startDate &&
    invoiceArg?.invoice?.type === 'INVOICE' &&
    getInvoiceContractType(invoiceArg) === 'TIME_TRACKING'
  );
};

export const isFixedRateWorkInvoice = (invoiceArg: IInvoiceInfo | IInvoiceWithContractAndCompany) => {
  return (
    invoiceArg?.invoice?.startDate &&
    invoiceArg?.invoice?.type === 'INVOICE' &&
    getInvoiceContractType(invoiceArg) === 'FIXED_RATE'
  );
};

export const isAutoApproved = (invoiceArg: IInvoiceInfo | IInvoiceWithContractAndCompany) => {
  return invoiceArg?.invoice?.autoApproved;
};

export const isOneOffPaymentInvoice = (invoiceArg: IInvoiceInfo | IInvoiceWithContractAndCompany | IInvoice) => {
  const invoiceInfo = invoiceArg as IInvoiceInfo | IInvoiceWithContractAndCompany;
  const invoice = invoiceArg as IInvoice;
  if (invoiceInfo?.invoice) {
    return !invoiceInfo?.invoice?.startDate && invoiceInfo?.invoice?.type === 'INVOICE';
  }
  return !invoice?.startDate && invoice?.type === 'INVOICE';
};

export const isMilestoneInvoice = (invoiceArg: IInvoiceInfo | IInvoiceWithContractAndCompany) => {
  return invoiceArg?.contract.contractType === 'MILESTONE' && invoiceArg?.invoice.type === 'MILESTONE';
};

export const isItemTypeInvoice = (invoiceInfo: IInvoiceInfo) => {
  return Object.keys(ItemTypeInvoices).includes(invoiceInfo?.invoice?.type);
};

export const isWorkTypeInvoice = (invoiceInfo: IInvoiceInfo) => {
  return (
    isFlexibleRateWorkInvoice(invoiceInfo) ||
    isFixedRateWorkInvoice(invoiceInfo) ||
    isMilestoneInvoice(invoiceInfo) ||
    isTimeTrackingWorkInvoice(invoiceInfo)
  );
};

export const isInvoiceOpen = (invoiceArg: IInvoiceInfo | IInvoiceWithContractAndCompany | IInvoice) => {
  const invoiceInfo = invoiceArg as IInvoiceInfo | IInvoiceWithContractAndCompany;
  const invoice = invoiceArg as IInvoice;
  const openStatuses = [InvoiceStatus.REQUIRES_ACTION, InvoiceStatus.OPEN];
  if (invoiceInfo?.invoice) {
    return openStatuses.includes(invoiceInfo.invoice.status as InvoiceStatus);
  }
  return openStatuses.includes(invoice?.status as InvoiceStatus);
};

export const isInvoiceUnapproved = (invoiceArg: IInvoiceInfo | IInvoiceWithContractAndCompany | IInvoice) => {
  const invoiceInfo = invoiceArg as IInvoiceInfo | IInvoiceWithContractAndCompany;
  const invoice = invoiceArg as IInvoice;
  if (invoiceInfo.invoice) {
    return invoiceInfo.invoice.approvalStatus === 'UNAPPROVED';
  }
  return invoice.approvalStatus === 'UNAPPROVED';
};

export const isInvoiceApproved = (invoiceArg: IInvoiceInfo | IInvoiceWithContractAndCompany | IInvoice) => {
  const invoiceInfo = invoiceArg as IInvoiceInfo | IInvoiceWithContractAndCompany;
  const invoice = invoiceArg as IInvoice;
  if (invoiceInfo.invoice) {
    return invoiceInfo.invoice.approvalStatus === 'APPROVED';
  }
  return invoice.approvalStatus === 'APPROVED';
};

/**
 * Whether an invoice still needs an approval before it can be paid. A null approval status is an
 * invoice nobody has acted on yet, so it counts as pending approval alongside UNAPPROVED.
 */
export const invoiceNeedsApproval = (invoiceArg: IInvoiceInfo | IInvoiceWithContractAndCompany | IInvoice) => {
  const invoiceInfo = invoiceArg as IInvoiceInfo | IInvoiceWithContractAndCompany;
  const invoice = invoiceArg as IInvoice;
  const approvalStatus = invoiceInfo?.invoice ? invoiceInfo.invoice.approvalStatus : invoice?.approvalStatus;
  return approvalStatus === 'UNAPPROVED' || approvalStatus === null;
};

export const isInvoiceInCurrentCycle = (
  invoiceArg: IInvoiceInfo | IInvoiceWithContractAndCompany,
  payCycle: PayCycleEstimate,
) => {
  return invoiceArg.invoice.startDate === payCycle?.startDate && invoiceArg.invoice.endDate === payCycle?.endDate;
};

export const isInvoicePaid = (invoiceArg: IInvoiceInfo | IInvoiceWithContractAndCompany | IInvoice) => {
  const invoiceInfo = invoiceArg as IInvoiceInfo | IInvoiceWithContractAndCompany;
  const invoice = invoiceArg as IInvoice;
  if (invoiceInfo.invoice) {
    return invoiceStatusTextStyle[invoiceInfo.invoice.status]?.text.toUpperCase() === 'PAID';
  }
  return invoiceStatusTextStyle[invoice.status]?.text.toUpperCase() === 'PAID';
};

export const isInvoiceProcessing = (invoiceArg: IInvoiceInfo | IInvoiceWithContractAndCompany | IInvoice) => {
  const invoiceInfo = invoiceArg as IInvoiceInfo | IInvoiceWithContractAndCompany;
  const invoice = invoiceArg as IInvoice;
  if (invoiceInfo.invoice) {
    return invoiceStatusTextStyle[invoiceInfo.invoice.status]?.text.toUpperCase() === 'PROCESSING';
  }
  return invoiceStatusTextStyle[invoice.status]?.text.toUpperCase() === 'PROCESSING';
};

export const isInvoiceVoidOrFailed = (invoiceArg: IInvoiceInfo | IInvoiceWithContractAndCompany | IInvoice) => {
  /* NOTE: This definition is used to categorize invoices. Right now void and failed 
  invoices belong to the same category
   */
  const invoiceInfo = invoiceArg as IInvoiceInfo | IInvoiceWithContractAndCompany;
  const invoice = invoiceArg as IInvoice;
  if (invoiceInfo.invoice) {
    return (
      invoiceStatusTextStyle[invoiceInfo.invoice.status]?.text.toUpperCase() === 'VOID' ||
      invoiceStatusTextStyle[invoiceInfo.invoice.status]?.text.toUpperCase() === 'FAILED'
    );
  }
  return (
    invoiceStatusTextStyle[invoice.status]?.text.toUpperCase() === 'VOID' ||
    invoiceStatusTextStyle[invoice.status]?.text.toUpperCase() === 'FAILED'
  );
};

export const isInvoiceFailed = (invoiceArg: IInvoiceInfo | IInvoiceWithContractAndCompany | IInvoice) => {
  const invoiceInfo = invoiceArg as IInvoiceInfo | IInvoiceWithContractAndCompany;
  const invoice = invoiceArg as IInvoice;
  if (invoiceInfo.invoice) {
    return invoiceStatusTextStyle[invoiceInfo.invoice.status]?.text.toUpperCase() === 'FAILED';
  }
  return invoiceStatusTextStyle[invoice.status]?.text.toUpperCase() === 'FAILED';
};

/**
 * Totals invoices only when they all share one payment currency. `actualPaymentCurrency` is per
 * invoice, so a mixed set has no single figure and callers should show the count on its own.
 */
export const sumInvoicePaymentAmounts = (
  invoices: IInvoiceInfo[],
  fallbackCurrency: string,
): { amount: number; currency: string } | null => {
  const rows = invoices || [];
  const currencies = new Set(rows.map((invoiceInfo) => invoiceInfo.invoice.actualPaymentCurrency).filter(Boolean));
  if (currencies.size > 1) return null;
  return {
    amount: rows.reduce((sum, invoiceInfo) => sum + (invoiceInfo.invoice.actualPaymentAmount || 0), 0),
    currency: [...currencies][0] ?? fallbackCurrency,
  };
};

/** Window shown in settings when neither team nor company has one set. Payability itself treats unset as 0. */
export const DEFAULT_INVOICE_AVAILABILITY_DAYS = 10;

export const isInvoiceInPayableDateRange = (
  invoiceInfo: IInvoiceInfo,
  company: ICompany,
  /** Optional per-invoice override resolver. When provided, the team's override takes precedence over the company default. */
  getDaysForInvoice?: (invoiceInfo: IInvoiceInfo) => number | undefined,
) => {
  const companyDays = company?.settings?.paymentAllowedDaysBeforeDueDate;
  const allowedDays = getDaysForInvoice?.(invoiceInfo) ?? companyDays;
  return isInvoiceDueOrWithinAllowedWindow(invoiceInfo.invoice.paymentDueDate, allowedDays);
};

export interface IOpenInvoiceCounts {
  /** Open + unapproved (or null approval status) invoices that are due or within the allowed payment window. */
  openInvoicesCount: number;
  /** Open + approved invoices that are due or within the allowed payment window. */
  approvedInvoicesCount: number;
  overdueButApprovedCount: number;
  overdueButNotApprovedCount: number;
  /** Open invoices that are not yet due (future-due beyond the allowed payment window). */
  notDueInvoicesCount: number;
  /** Approved subset of `notDueInvoicesCount`. */
  notDueApprovedCount: number;
  /** Unapproved (or null approval status) subset of `notDueInvoicesCount`. */
  notDueUnapprovedCount: number;
}

/**
 * Returns the open invoices that are "Not Due": future-due beyond the configured invoice-availability window
 * (i.e. not overdue, not due today, and not within the allowed days before the due date).
 *
 * `getDaysForInvoice` optionally resolves the invoice-availability window per invoice (team-level override),
 * falling back to `companyInvoiceAvailabilityDaysCount`.
 */
export const getNotDueInvoices = (
  invoices: IInvoiceInfo[],
  companyInvoiceAvailabilityDaysCount?: number,
  getDaysForInvoice?: (invoiceInfo: IInvoiceInfo) => number | undefined,
): IInvoiceInfo[] =>
  invoices.filter((invoiceInfo) => {
    const allowedDays = getDaysForInvoice?.(invoiceInfo) ?? companyInvoiceAvailabilityDaysCount;
    return (
      isInvoiceOpen(invoiceInfo) && !isInvoiceDueOrWithinAllowedWindow(invoiceInfo.invoice.paymentDueDate, allowedDays)
    );
  });

/**
 * Builds a per-invoice resolver for the invoice-availability window. The resolver looks up the invoice's
 * team via its contract id; if that team has its own `paymentAllowedDaysBeforeDueDate`, that value is
 * returned. Otherwise `undefined` is returned so callers fall back to the company-level default.
 */
export const buildInvoiceAvailabilityDaysResolver = ({
  teams,
  teamsInfo,
}: {
  teams?: Record<string, TeamAndDetails>;
  teamsInfo?: ICompany['teamsById'];
}): ((invoiceInfo: IInvoiceInfo) => number | undefined) | undefined => {
  if (!teams || !teamsInfo) return undefined;
  return (invoiceInfo: IInvoiceInfo) => {
    const contractID = invoiceInfo?.invoice?.contractID;
    if (!contractID) return undefined;
    const teamId = getTeamByContractId(contractID, teams)?.id;
    if (!teamId) return undefined;
    return teamsInfo[teamId]?.paymentAllowedDaysBeforeDueDate ?? undefined;
  };
};

/**
 * Computes open-invoice count stats over an arbitrary invoice set. Callers pass
 * either a whole company's open invoices or a filtered subset (e.g. team-scoped).
 *
 * `getDaysForInvoice` optionally resolves the invoice-availability window per invoice (used when the
 * team-level override differs from the company default). When it returns undefined for an invoice,
 * we fall back to `companyInvoiceAvailabilityDaysCount`.
 */
export const getOpenInvoiceCounts = (
  invoices: IInvoiceInfo[],
  companyInvoiceAvailabilityDaysCount?: number,
  getDaysForInvoice?: (invoiceInfo: IInvoiceInfo) => number | undefined,
): IOpenInvoiceCounts => {
  let openUnapprovedInvoicesCount = 0;
  let approvedInvoicesCount = 0;
  let overdueButApprovedCount = 0;
  let overdueButNotApprovedCount = 0;
  let notDueInvoicesCount = 0;
  let notDueApprovedCount = 0;
  let notDueUnapprovedCount = 0;

  invoices.forEach((invoiceInfo) => {
    const allowedDays = getDaysForInvoice?.(invoiceInfo) ?? companyInvoiceAvailabilityDaysCount;
    if (
      isInvoiceOpen(invoiceInfo) &&
      !isInvoiceDueOrWithinAllowedWindow(invoiceInfo.invoice.paymentDueDate, allowedDays)
    ) {
      notDueInvoicesCount++;
      if (isInvoiceApproved(invoiceInfo)) {
        notDueApprovedCount++;
      } else {
        notDueUnapprovedCount++;
      }
    }
    if (
      isInvoiceOpen(invoiceInfo) &&
      isInvoiceApproved(invoiceInfo) &&
      isInvoiceDueOrWithinAllowedWindow(invoiceInfo.invoice.paymentDueDate, allowedDays)
    ) {
      approvedInvoicesCount++;
    }
    if (
      isInvoiceOpen(invoiceInfo) &&
      invoiceNeedsApproval(invoiceInfo) &&
      isInvoiceDueOrWithinAllowedWindow(invoiceInfo.invoice.paymentDueDate, allowedDays)
    ) {
      openUnapprovedInvoicesCount++;
    }
    if (
      checkIfInvoiceOverdue(invoiceInfo?.invoice.paymentDueDate) &&
      isInvoiceOpen(invoiceInfo) &&
      !isMilestoneInvoice(invoiceInfo)
    ) {
      if (isInvoiceApproved(invoiceInfo)) {
        overdueButApprovedCount++;
      } else {
        overdueButNotApprovedCount++;
      }
    }
  });

  return {
    openInvoicesCount: openUnapprovedInvoicesCount,
    approvedInvoicesCount,
    overdueButApprovedCount,
    overdueButNotApprovedCount,
    notDueInvoicesCount,
    notDueApprovedCount,
    notDueUnapprovedCount,
  };
};

/*
- InvoiceStats are used to display information on the Invoice Cycle blocks (at Invoice History, Review/Pay Pages, etc)  and InvoiceBreakupModal
- id parameter is of the format uniqueValue_groupType_startDate_endDate where groupType is PAYCYCLE
and uniqueValue_groupType_invoiceDisplayID for MILESTONE AND ITEMORONEOFF
*/
export const getInvoicesStats = (id: string, invoicesArg: IInvoiceInfo[] | IInvoice[]) => {
  const invoicesInfo = invoicesArg as IInvoiceInfo[];
  const allInvoices = invoicesArg as IInvoice[];

  const firstInvoice = invoicesInfo[0].invoice ? invoicesInfo[0].invoice : allInvoices[0];
  const contractType = invoicesInfo[0].contract ? contractTypeMap[invoicesInfo[0].contract.contractType] : undefined;
  const userID = invoicesInfo[0].recipientUser ? invoicesInfo[0].recipientUser.userID : undefined;
  const firstName = invoicesInfo[0].recipientUser ? invoicesInfo[0].recipientUser.firstName : undefined;
  const lastName = invoicesInfo[0].recipientUser ? invoicesInfo[0].recipientUser.lastName : undefined;

  // Map to accumulate amounts per currency
  const currencyMap: Record<string, number> = {};
  // Helper to add amount to currencyMap
  const addToCurrencyMap = (currency: string, amount: number) => {
    if (!currencyMap[currency]) currencyMap[currency] = 0;
    currencyMap[currency] += amount;
  };

  // We'll fill this after processing invoices
  const invoiceCycleStats: IInvoiceStats = {
    userID: userID,
    firstName: firstName,
    lastName: lastName,
    totalAmount: 0,
    contractID: firstInvoice.contractID,
    approvedCount: 0,
    pendingReviewCount: 0,
    overdueCount: 0,
    processingCount: 0,
    paidCount: 0,
    voidOrFailedCount: 0,
    invoiceCycleStartDate: firstInvoice.startDate,
    invoiceCycleEndDate: firstInvoice.endDate,
    invoiceCycleDueDate: firstInvoice.paymentDueDate,
    currency: firstInvoice.currency,
    contractType: contractType,
    groupType: undefined,
    allPaid: undefined,
    status: undefined,
    paymentTimestamp: undefined,
    type: undefined,
    updatedAt: undefined,
    currencyAmounts: [], // Will be filled below
  };
  const groupType = id.split('_')[1];
  if (groupType === 'PAYCYCLE') {
    /*
    calculate stats for invoices that are part of a Pay Cycle (Regular Invoices and Item Type Invoices for Flexible and Fixed Rate Contractors)
    */
    invoiceCycleStats.groupType = 'PAYCYCLE';
    (invoicesInfo || allInvoices).forEach((_invoiceInfoOrInvoice: IInvoiceInfo | IInvoice) => {
      const _invoiceInfo = _invoiceInfoOrInvoice as IInvoiceInfo;
      const _invoice = _invoiceInfoOrInvoice as IInvoice;

      let invoice: IInvoice;
      if (_invoiceInfo.invoice) {
        invoice = _invoiceInfo?.invoice;
      } else {
        invoice = _invoice;
      }

      if (invoiceCycleStats.updatedAt) {
        if (invoiceCycleStats.updatedAt < invoice.updatedAt) {
          invoiceCycleStats.updatedAt = invoice.updatedAt;
        }
      } else {
        invoiceCycleStats.updatedAt = invoice.updatedAt || invoice.creationTimestamp;
      }

      if (!isInvoiceVoidOrFailed(invoice)) {
        addToCurrencyMap(invoice.currency, invoice.amountMinusDeductions);
      } else {
        invoiceCycleStats.voidOrFailedCount++;
      }
      if (isInvoiceOpen(invoice)) {
        if (isInvoiceApproved(invoice)) {
          invoiceCycleStats.approvedCount++;
        } else if (isInvoiceUnapproved(invoice)) {
          invoiceCycleStats.pendingReviewCount++;
        }
        if (checkIfInvoiceOverdue(invoice.paymentDueDate)) {
          invoiceCycleStats.overdueCount++;
        }
      } else if (isInvoicePaid(invoice)) {
        invoiceCycleStats.paidCount++;
      } else if (invoice.status === 'PROCESSING') {
        invoiceCycleStats.processingCount++;
      }
    });
    // Prepare currencyAmounts array
    invoiceCycleStats.currencyAmounts = Object.entries(currencyMap).map(([currency, amount]) => ({ currency, amount }));
    if (invoiceCycleStats.currencyAmounts.length === 1) {
      // Single currency, keep legacy fields
      invoiceCycleStats.totalAmount = invoiceCycleStats.currencyAmounts[0].amount;
      invoiceCycleStats.currency = invoiceCycleStats.currencyAmounts[0].currency;
    } else if (invoiceCycleStats.currencyAmounts.length > 1) {
      // Multi-currency: set totalAmount to 0 and currency to 'MULTI' for clarity
      invoiceCycleStats.totalAmount = 0;
      invoiceCycleStats.currency = 'MULTI';
    } else {
      // No valid (non-void/failed) invoices
      invoiceCycleStats.totalAmount = 0;
      invoiceCycleStats.currency = firstInvoice.currency;
      invoiceCycleStats.currencyAmounts = [];
    }
    if (
      invoiceCycleStats.approvedCount === 0 &&
      invoiceCycleStats.pendingReviewCount === 0 &&
      invoiceCycleStats.processingCount === 0 &&
      invoiceCycleStats.voidOrFailedCount === 0 &&
      invoiceCycleStats.paidCount > 0
    ) {
      invoiceCycleStats.allPaid = true;
    }
    invoiceCycleStats.status = getInvoiceCycleStatus(invoiceCycleStats);
  } else {
    /*
    calculate stats for invoices that are NOT part of a Pay Cycle (One-Off Payments, and
    Milestone Invoices and Item Type Invoices for Milestone Contractors)

    Note: these types of invoices only have 1 invoice in each invoicesInfo object
    */
    (invoicesInfo || allInvoices).forEach((_invoiceInfoOrInvoice: IInvoiceInfo | IInvoice) => {
      const _invoiceInfo = _invoiceInfoOrInvoice as IInvoiceInfo;
      const _invoice = _invoiceInfoOrInvoice as IInvoice;

      let invoice: IInvoice;
      if (_invoiceInfo.invoice) {
        invoice = _invoiceInfo?.invoice;
      } else {
        invoice = _invoice;
      }

      if (groupType === 'MILESTONE') {
        invoiceCycleStats.groupType = 'MILESTONE';
      } else {
        invoiceCycleStats.type = isOneOffPaymentInvoice(invoice) ? 'One-Off Payment' : invoice.type;
        invoiceCycleStats.groupType = 'ITEMORONEOFF';
      }

      invoiceCycleStats.updatedAt = invoice.updatedAt || invoice.creationTimestamp;
      invoiceCycleStats.approvedCount = 0;
      invoiceCycleStats.pendingReviewCount = 0;
      addToCurrencyMap(invoice.currency, invoice.amountMinusDeductions);

      if (!isInvoicePaid(invoice)) {
        if (isInvoiceVoidOrFailed(invoice)) {
          invoiceCycleStats.status = 'VOID';
        } else if (invoice.status === 'PROCESSING') invoiceCycleStats.status = 'PROCESSING';
        else invoiceCycleStats.status = 'DUE';
      } else {
        invoiceCycleStats.status = 'PAID';
        invoiceCycleStats.allPaid = true;
      }
    });
    // Prepare currencyAmounts array
    invoiceCycleStats.currencyAmounts = Object.entries(currencyMap).map(([currency, amount]) => ({ currency, amount }));
    if (invoiceCycleStats.currencyAmounts.length === 1) {
      invoiceCycleStats.totalAmount = invoiceCycleStats.currencyAmounts[0].amount;
      invoiceCycleStats.currency = invoiceCycleStats.currencyAmounts[0].currency;
    } else if (invoiceCycleStats.currencyAmounts.length > 1) {
      invoiceCycleStats.totalAmount = 0;
      invoiceCycleStats.currency = 'MULTI';
    } else {
      // No valid invoices (all void/failed)
      invoiceCycleStats.totalAmount = 0;
      invoiceCycleStats.currency = firstInvoice.currency;
    }
  }
  return invoiceCycleStats;
};

const getInvoiceCycleStatus = (invoiceCycleStats: IInvoiceStats) => {
  let status = 'DUE';
  if (invoiceCycleStats.approvedCount === 0 && invoiceCycleStats.pendingReviewCount === 0) {
    if (invoiceCycleStats.processingCount > 0) status = 'PROCESSING';
    else if (invoiceCycleStats.paidCount > 0) status = 'PAID';
    else if (invoiceCycleStats.paidCount === 0) status = 'VOID';
  } else if (invoiceCycleStats.overdueCount > 0) {
    status = 'OVERDUE';
  } else if (!checkIfInvoiceDue(invoiceCycleStats?.invoiceCycleDueDate)) {
    status = 'NOT_DUE';
  } else {
    status = 'DUE';
  }
  return status as IInvoiceStats['status'];
};

/**
 * Returns sorted milestones object, with completed milestones last
 */
export const getMilestonesSortedByStatus = (milestones: { [key: string]: IMilestone }) => {
  if (!milestones || !Object.keys(milestones)?.length) return {};
  // Separate completed and incomplete milestones
  const completedMilestones = {};
  const incompleteMilestones = {};

  for (const key in milestones) {
    const milestone = milestones[key];
    if (milestone.status === 'COMPLETE') {
      completedMilestones[key] = milestone;
    } else {
      incompleteMilestones[key] = milestone;
    }
  }

  // Combine completed and incomplete milestones while putting completed ones last
  const sortedMilestones = { ...incompleteMilestones, ...completedMilestones };

  return sortedMilestones;
};

/** Returns payment due string for display */
export const getPaymentDueString = (paymentDueDays: number) => {
  if (!paymentDueDays || paymentDueDays === 0) {
    return 'Same day';
  }
  return `${paymentDueDays} day${paymentDueDays > 1 ? 's' : ''} later`;
};

export const getOrdinal = (n: number) => {
  const s = ['th', 'st', 'nd', 'rd'],
    v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

/** Returns cycle ends on string for display */
export const getCycleEndString = (cycleEnd: {
  type: string;
  dayOfWeek?: string;
  value?: { type: string; value: any };
}): string => {
  const { type, value, dayOfWeek } = cycleEnd || {};

  try {
    if (type === 'weekly' || type === 'biweekly') {
      // Convert the dayOfWeek to title case
      const formattedDayOfWeek = dayOfWeek.charAt(0) + dayOfWeek.slice(1).toLowerCase();
      if (type === 'weekly') {
        return 'Every ' + formattedDayOfWeek;
      }
      return 'Every other ' + formattedDayOfWeek;
    }

    if (type === 'semimonthly') {
      if (value.type === 'fifteenthAndEndOfMonth') {
        return 'On the 15th and last day of the month';
      }
      // else value.type = dayOfMonth
      return 'On the ' + getOrdinal(value.value) + ' of the month';
    }

    if (type === 'monthly') {
      if (value.type === 'lastDayOfMonth') {
        return 'On the last day of the month';
      }
      if (value.type === 'dayOfMonth') {
        return 'On the ' + getOrdinal(value.value) + ' of the month';
      }
      // else value.type = lastDayOfWeekOfMonth
      return 'On the last ' + value.value + ' of the month';
    }
  } catch (error) {
    console.error(error);
    captureErrorInDatadog(error, { data: cycleEnd, source: 'Error in getCycleEndString' });
    return '';
  }
};

export const getInvoiceStatus = (invoiceArg: IInvoiceInfo | IInvoice): InvoiceStatus | 'APPROVED' => {
  const invoiceInfo = invoiceArg as IInvoiceInfo;
  const invoice = invoiceArg as IInvoice;

  if (invoiceInfo.invoice) {
    const status = invoiceInfo.invoice.status;
    if (invoiceInfo.invoice.isOpen && invoiceInfo.invoice.isApproved) {
      return 'APPROVED';
    } else {
      return status;
    }
  }

  if (invoice.isOpen && invoice.isApproved) {
    return 'APPROVED';
  } else {
    return invoice.status;
  }
};

export const getDueStatus = (invoiceArg: IInvoiceInfo | IInvoice): keyof typeof DueStatus => {
  const invoiceInfo = invoiceArg as IInvoiceInfo;
  const invoice = invoiceArg as IInvoice;

  if (invoiceInfo?.invoice) {
    if (checkIfInvoiceOverdue(invoiceInfo.invoice.paymentDueDate)) {
      return 'OVERDUE';
    } else if (checkIfInvoiceDue(invoiceInfo.invoice.paymentDueDate) || !invoiceInfo.invoice.startDate) {
      return 'DUE';
    } else {
      return 'NOT_DUE';
    }
  }

  if (checkIfInvoiceOverdue(invoice.paymentDueDate)) {
    return 'OVERDUE';
  } else if (checkIfInvoiceDue(invoice.paymentDueDate) || !invoice.startDate) {
    return 'DUE';
  } else {
    return 'NOT_DUE';
  }
};

export const showDueStatus = (invoiceArg: IInvoiceInfo | IInvoice) => {
  const invoiceInfo = invoiceArg as IInvoiceInfo;
  const invoice = invoiceArg as IInvoice;

  if (invoiceInfo.invoice) {
    if (isInvoicePaid(invoiceInfo) || isInvoiceVoidOrFailed(invoiceInfo)) {
      return false;
    }
    return true;
  }

  if (isInvoicePaid(invoice) || isInvoiceVoidOrFailed(invoice)) {
    return false;
  }
  return true;
};

export const getCurrentTeamInvoices = ({
  invoices,
  teams,
  teamIdsToFilter,
}: {
  invoices: IInvoiceInfo[];
  teams: Record<string, TeamAndDetails>;
  teamIdsToFilter: string[];
}) => {
  if (!Object.keys(teams)?.length) return [];

  const _currentTeamInvoices = invoices?.filter((invoiceInfo) => {
    const contractID = invoiceInfo?.invoice.contractID;
    const currentInvoiceTeamID = getTeamByContractId(contractID, teams)?.id;

    if (!teamIdsToFilter?.length) {
      return true;
    } else {
      return teamIdsToFilter.includes(currentInvoiceTeamID);
    }
  });

  return _currentTeamInvoices;
};

export const getActionPendingInvoices = ({ invoices }: { invoices: IInvoiceInfo[] }) => {
  const _actionPendingInvoices = [];
  invoices?.forEach((invoice) => {
    const isOpen = isInvoiceOpen(invoice);

    if (invoice.invoice.approvalStatus !== 'DENIED' && isOpen) {
      _actionPendingInvoices.push(invoice);
    }
  });

  return _actionPendingInvoices;
};

export const getInvoiceType = (invoiceArg: IInvoiceInfo | IInvoiceWithContractAndCompany) => {
  if (isFlexibleRateWorkInvoice(invoiceArg)) {
    return 'Pay As You Go';
  }

  if (isFixedRateWorkInvoice(invoiceArg)) {
    return 'Fixed';
  }

  if (isOneOffPaymentInvoice(invoiceArg)) {
    return 'One-Off';
  }

  if (isMilestoneInvoice(invoiceArg)) {
    return 'Milestone';
  }

  if (isItemTypeInvoice(invoiceArg)) {
    return invoiceArg.invoice.type;
  }

  // Fallback case
  return invoiceArg.invoice.type;
};

export interface InvoiceBreakdown {
  contractorMetadata: { title: string; value: string }[]; // Order: CONTRACTORS NAME -> CID NO -> LOB
  amountMetadata: { title: string; value: string }[]; // Order: As defined in amountMetadataOrder, followed by remaining fields
}

// Helper function to organize invoice breakdown data
export const organize1840InvoiceBreakdown = (
  amountBreakdown: Record<string, string>,
  invoice?: IInvoice,
  _recipientUserEmail?: string,
): InvoiceBreakdown => {
  if (!amountBreakdown || Object.keys(amountBreakdown).length === 0)
    return { contractorMetadata: [], amountMetadata: [] };

  // HACKY FIX: Workaround for backend issue with payment intent pi_3Sei9ACw67GoJ0rW0e8auiWS
  // Check if this invoice has the problematic payment intent ID
  const stripePaymentIntentID =
    (invoice as any)?.stripePaymentIntentID ||
    amountBreakdown['stripePaymentIntentID'] ||
    amountBreakdown['Stripe Payment Intent ID'];

  const PROBLEMATIC_PAYMENT_INTENT_ID = 'pi_3SkRUMCw67GoJ0rW0tmI4tgJ';
  const PROBLEMATIC_PAYMENT_INTENT_ID_2 = 'pi_3SlCXSCw67GoJ0rW0ZvPLoIK';

  // Create a copy to avoid mutating the original
  const processedBreakdown = { ...amountBreakdown };

  if (stripePaymentIntentID === PROBLEMATIC_PAYMENT_INTENT_ID) {
    // Check if fields are actually messed up before applying remapping
    // Only remap if expected fields are missing/empty AND wrong fields have values
    const hourlyRateValue = processedBreakdown['HOURLY RATE'] || processedBreakdown['Hourly Rate'] || '';
    const regHoursValue =
      processedBreakdown['REG HOURS'] || processedBreakdown['Reg Hours'] || processedBreakdown['Regular Hours'] || '';
    const workedHrsRateValue =
      processedBreakdown['WORKED HRS RATE'] ||
      processedBreakdown['Worked Hrs Rate'] ||
      processedBreakdown['Worked Hours Rate'] ||
      '';
    const grossPayValue = processedBreakdown['GROSS PAY'] || processedBreakdown['Gross Pay'] || '';

    const adjustmentValue = processedBreakdown['ADJUSTMENT'] || processedBreakdown['Adjustment'] || '';
    const addHoursValue =
      processedBreakdown['ADD HOURS'] ||
      processedBreakdown['Add Hours'] ||
      processedBreakdown['Additional Hours'] ||
      '';
    const addHoursPayValue =
      processedBreakdown['ADD HOURS PAY'] ||
      processedBreakdown['Add Hours Pay'] ||
      processedBreakdown['Additional Hours Pay'] ||
      '';
    const companyLoanValue = processedBreakdown['Company Loan'] || processedBreakdown['Company loan'] || '';

    // Only apply remapping if expected fields are missing/empty AND wrong fields have values
    const isMessedUp =
      (!hourlyRateValue || !regHoursValue || !workedHrsRateValue || !grossPayValue) &&
      (adjustmentValue || addHoursValue || addHoursPayValue || companyLoanValue);

    if (isMessedUp) {
      // Remap fields: ADJUSTMENT -> HOURLY RATE, ADD HOURS -> REG HOURS, ADD HOURS PAY -> WORKED HRS RATE, Company Loan -> GROSS PAY
      // Set the correct fields only if they're missing
      if (!hourlyRateValue && adjustmentValue) {
        processedBreakdown['HOURLY RATE'] = adjustmentValue;
        processedBreakdown['Hourly Rate'] = adjustmentValue;
      }
      if (!regHoursValue && addHoursValue) {
        processedBreakdown['REG HOURS'] = addHoursValue;
        processedBreakdown['Reg Hours'] = addHoursValue;
        processedBreakdown['Regular Hours'] = addHoursValue;
      }
      if (!workedHrsRateValue && addHoursPayValue) {
        processedBreakdown['WORKED HRS RATE'] = addHoursPayValue;
        processedBreakdown['Worked Hrs Rate'] = addHoursPayValue;
        processedBreakdown['Worked Hours Rate'] = addHoursPayValue;
      }
      if (!grossPayValue && companyLoanValue) {
        processedBreakdown['GROSS PAY'] = companyLoanValue;
        processedBreakdown['Gross Pay'] = companyLoanValue;
      }

      // Clear/reset the incorrectly populated fields only if we used them for remapping
      if (adjustmentValue && !hourlyRateValue) {
        processedBreakdown['ADJUSTMENT'] = '';
        processedBreakdown['Adjustment'] = '';
      }
      if (addHoursValue && !regHoursValue) {
        processedBreakdown['ADD HOURS'] = '-';
        processedBreakdown['Add Hours'] = '-';
        processedBreakdown['Additional Hours'] = '-';
      }
      if (addHoursPayValue && !workedHrsRateValue) {
        processedBreakdown['ADD HOURS PAY'] = '-';
        processedBreakdown['Add Hours Pay'] = '-';
        processedBreakdown['Additional Hours Pay'] = '-';
      }
      if (companyLoanValue && !grossPayValue) {
        processedBreakdown['Company Loan'] = '';
      }
    }
  }

  // HACKY FIX: Workaround for backend issue with payment intent pi_3SlCXSCw67GoJ0rW0ZvPLoIK
  // Reimbursement was populated instead of BONUS, remap if Details is NOT equal to "Reimbursement"
  if (stripePaymentIntentID === PROBLEMATIC_PAYMENT_INTENT_ID_2) {
    const bonusValue = processedBreakdown['BONUS'] || processedBreakdown['Bonus'] || '';
    const reimbursementValue = processedBreakdown['Reimbursement'] || '';
    const detailsValue =
      processedBreakdown['Details'] || processedBreakdown['details'] || processedBreakdown['DETAILS'] || '';

    // Check if BONUS is missing/empty, Reimbursement has value, and Details is NOT equal to "Reimbursement"
    if (!bonusValue && reimbursementValue && detailsValue !== 'Reimbursement') {
      // Remap Reimbursement -> BONUS
      processedBreakdown['BONUS'] = reimbursementValue;
      processedBreakdown['Bonus'] = reimbursementValue;

      // Clear Reimbursement after remapping
      processedBreakdown['Reimbursement'] = '';
    }
  }

  // Define which fields belong to contractor metadata with possible variations and their order
  const contractorMetadataFields = [
    {
      key: 'CONTRACTORS NAME',
      variations: ['Contractors Name', "Contractor's Name", 'Contractor Name'],
    },
    {
      key: 'CID NO',
      variations: ['CID NO', 'Cid No'],
    },
    {
      key: 'LOB',
      variations: ['LOB', 'Lob'],
    },
    {
      key: 'Period',
      variations: ['Period', 'period', 'PERIOD'],
    },
  ];

  // Define the exact order for amount metadata fields with possible variations
  const amountMetadataOrder = [
    {
      key: 'HOURLY RATE',
      variations: ['HOURLY RATE', 'Hourly Rate'],
    },
    {
      key: 'REG HOURS',
      variations: ['REG HOURS', 'Reg Hours', 'Regular Hours'],
    },
    {
      key: 'WORKED HRS RATE',
      variations: ['WORKED HRS RATE', 'Worked Hrs Rate', 'Worked Hours Rate'],
    },
    {
      key: 'ADJUSTMENT',
      variations: ['ADJUSTMENT', 'Adjustment'],
    },
    {
      key: 'ADD HOURS',
      variations: ['ADD HOURS', 'Add Hours', 'Additional Hours'],
    },
    {
      key: 'ADD HOURS PAY',
      variations: ['ADD HOURS PAY', 'Add Hours Pay', 'Additional Hours Pay'],
    },
    {
      key: 'GROSS PAY',
      variations: ['GROSS PAY', 'Gross Pay'],
    },
    {
      key: 'Bonus',
      variations: ['BONUS', 'Bonus'],
    },
    {
      key: 'Other Allowances',
      variations: ['OTHER ALLOWANCES', 'Other Allowances'],
    },
  ];

  // Transform input, filtering out only empty currency values
  const breakdown = Object.entries(processedBreakdown)
    .filter(([key, value]) => {
      // Only filter out empty values if the key is a currency code
      const isCurrencyCode = key.toUpperCase() in dineroCurrencyMapping;
      return !isCurrencyCode || (isCurrencyCode && value !== null && value !== '');
    })
    .map(([breakdownTitle, breakdownAmount]) => ({
      title: startCase(breakdownTitle),
      amount: breakdownAmount,
    }));

  const result = {
    contractorMetadata: [] as { title: string; value: string }[],
    amountMetadata: [] as { title: string; value: string }[],
  };

  // First, organize contractor metadata in specified order
  contractorMetadataFields.forEach(({ key, variations }) => {
    const item = breakdown?.find((item) =>
      variations.some((variant) => variant.toLowerCase() === item.title.toLowerCase()),
    );
    if (item) {
      result.contractorMetadata.push({
        title: key,
        value: item.amount,
      });
    }
  });

  // Then, organize amount metadata in exact specified order
  amountMetadataOrder.forEach(({ key, variations }) => {
    const item = breakdown?.find((item) =>
      variations.some((variant) => variant.toLowerCase() === item.title.toLowerCase()),
    );
    // Include all ordered fields even if empty
    result.amountMetadata.push({
      title: key,
      value: item?.amount || '',
    });
  });

  // Add remaining fields that weren't in the predefined order
  breakdown?.forEach((item) => {
    const isContractorMetadata = contractorMetadataFields.some(({ variations }) =>
      variations.some((variant) => variant.toLowerCase() === item.title.toLowerCase()),
    );
    const isInAmountOrder = amountMetadataOrder.some(({ variations }) =>
      variations.some((variant) => variant.toLowerCase() === item.title.toLowerCase()),
    );

    if (!isContractorMetadata && !isInAmountOrder) {
      result.amountMetadata.push({
        title: item.title,
        value: item.amount,
      });
    }
  });

  return result;
};

/**
 * The backend grants PERM_SUBMIT_WORK on this flag alone, whatever the contract type. Deciding it
 * from the contract type instead offered Submit Work on contracts the API then refused.
 */
export const canClientSubmitWork = (contract?: { clientCanSubmitWork?: boolean } | null): boolean =>
  contract?.clientCanSubmitWork === true;
